"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { mutate as globalMutate } from "swr";
import type { UploadBatchStatus } from "@/types";
import { cfFetch } from "../api/apiClient";

function invalidateCashflowCaches() {
  globalMutate(
    (key: unknown) =>
      typeof key === "string" &&
      (key.includes("/transactions") || key.includes("/cashflow")),
  );
}

function detailMessage(detail: unknown): string | undefined {
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && "error" in detail) {
    const err = (detail as { error?: unknown }).error;
    if (typeof err === "string") return err;
  }
  return undefined;
}

export function useRecategorizeTransactions() {
  const [batchId, setBatchId] = useState<string | null>(null);
  const [status, setStatus] = useState<UploadBatchStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (
      !batchId ||
      status?.status === "complete" ||
      status?.status === "failed"
    ) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    intervalRef.current = setInterval(async () => {
      try {
        const res = await cfFetch(`/upload/${batchId}/status`);
        if (res.ok) {
          const data: UploadBatchStatus = await res.json();
          setStatus(data);
          if (data.status === "complete" || data.status === "failed") {
            clearInterval(intervalRef.current!);
            invalidateCashflowCaches();
          }
        }
      } catch {
        // ignore
      }
    }, 2000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [batchId, status?.status]);

  const startRecategorize = useCallback(async () => {
    setError(null);
    setInfo(null);
    setBatchId(null);
    setStatus(null);

    const res = await cfFetch("/classify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ recategorizeAll: true }),
    });

    let data: Record<string, unknown> = {};
    try {
      data = (await res.json()) as Record<string, unknown>;
    } catch {
      /* empty body */
    }

    if (!res.ok) {
      const msg =
        detailMessage(data.detail) ??
        (typeof data.error === "string" ? data.error : undefined) ??
        "Request failed";
      setError(msg);
      return;
    }

    if (data.status === "nothing_to_classify") {
      setInfo("No uncategorized transactions to auto-categorize.");
      invalidateCashflowCaches();
      return;
    }

    const bid = typeof data.batchId === "string" ? data.batchId : null;
    const count = typeof data.count === "number" ? data.count : 0;
    if (!bid || count === 0) {
      setInfo("No uncategorized transactions to auto-categorize.");
      invalidateCashflowCaches();
      return;
    }

    setBatchId(bid);
    setStatus({
      id: bid,
      status: "processing",
      totalRows: count,
      processedRows: 0,
      errors: [],
    });
  }, []);

  const reset = useCallback(() => {
    setBatchId(null);
    setStatus(null);
    setError(null);
    setInfo(null);
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, []);

  const progress =
    status && status.totalRows > 0
      ? Math.round((status.processedRows / status.totalRows) * 100)
      : 0;

  const isBusy =
    !!batchId && status?.status !== "complete" && status?.status !== "failed";

  return {
    startRecategorize,
    status,
    progress,
    error,
    info,
    reset,
    isBusy,
  };
}
